import { Link } from "react-router-dom";
import { Check, ArrowRight, X } from "lucide-react";
import SectionHeading from "@/components/SectionHeading";

const includes = [
  "The full Q-Wis Windows Application or chosen modules",
  "Base configuration for your operation",
  "Configuration training to accommodate occasional variables",
  "Standard data collection reports & recording training",
  "Data entry training",
  "Real-time system support",
  "Annual governance report on the system",
];

const compare = [
  { row: "Pricing model", qwis: "Location-based — predictable scaling", typical: "Per-user subscription", positive: true },
  { row: "Payment", qwis: "One-time payment + AMC", typical: "Recurring licensing burden", positive: true },
  { row: "Add more auditors", qwis: "No additional cost", typical: "Per-seat increase", positive: true },
  { row: "Hardware investment", qwis: "Single data operator can manage 720 auditors", typical: "One device per auditor", positive: true },
  { row: "Implementation support", qwis: "Configuration, training & ongoing governance", typical: "Often extra paid services", positive: true },
];

export default function Pricing() {
  return (
    <>
      <section data-testid="pricing-hero" className="bg-[#0A1128] py-24 text-white sm:py-32">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#E8772E]">
            Pricing
          </p>
          <h1 className="mt-4 max-w-4xl font-serif-display text-4xl font-medium leading-[1.05] sm:text-6xl lg:text-7xl">
            Pricing built so the math always makes sense.
          </h1>
          <p className="mt-6 max-w-3xl text-base leading-relaxed text-white/80 sm:text-lg">
            Q-Wis is licensed per <strong className="text-white">location</strong> — not
            per user. One-time payment plus an Annual Maintenance Contract.
            We tailor the package to your operation, so the only honest answer
            is: let's talk.
          </p>
        </div>
      </section>

      <section data-testid="pricing-core" className="bg-white py-20 sm:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <div className="rounded-lg border border-[#0A1128] bg-[#0A1128] p-10 text-white">
                <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#E8772E]">
                  The Q-Wis Package
                </p>
                <h2 className="mt-4 font-serif-display text-3xl leading-tight sm:text-4xl">
                  Contact us for pricing
                </h2>
                <p className="mt-4 text-sm leading-relaxed text-white/75">
                  Pricing depends on your number of locations, modules of
                  interest and operational scale. We respond with a tailored
                  proposal — no per-user games.
                </p>
                <Link
                  to="/contact?intent=pricing"
                  data-testid="pricing-cta-contact"
                  className="mt-8 inline-flex items-center gap-2 rounded-md bg-[#E8772E] px-6 py-3.5 text-sm font-semibold text-white hover:bg-[#D66826]"
                >
                  Get a tailored quote <ArrowRight size={16} />
                </Link>
                <p className="mt-6 text-xs uppercase tracking-[0.18em] text-white/50">
                  Typical response within 1–2 business days
                </p>
              </div>
            </div>

            <div className="lg:col-span-7">
              <h3 className="font-serif-display text-2xl text-[#0A1128] sm:text-3xl">
                What's included
              </h3>
              <ul className="mt-6 grid grid-cols-1 gap-3">
                {includes.map((t, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-4 rounded-lg border border-[#E2E8F0] bg-white p-5"
                  >
                    <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#E8772E]/10 text-[#E8772E]">
                      <Check size={16} />
                    </span>
                    <span className="text-sm leading-relaxed text-[#334155]">{t}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section data-testid="pricing-compare" className="bg-[#F8FAFC] py-20 sm:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeading
            eyebrow="Compare"
            title="Q-Wis vs. typical user-based quality SaaS."
            lede="Why predictable, location-based pricing changes the conversation in your boardroom."
          />

          <div className="mt-12 overflow-hidden rounded-lg border border-[#E2E8F0] bg-white">
            <div className="hidden grid-cols-12 border-b border-[#E2E8F0] bg-[#F8FAFC] px-6 py-4 text-xs font-semibold uppercase tracking-[0.18em] text-[#475569] md:grid">
              <div className="col-span-4">Dimension</div>
              <div className="col-span-4">Q-Wis</div>
              <div className="col-span-4">Typical SaaS</div>
            </div>
            {compare.map((c, i) => (
              <div
                key={i}
                className="grid grid-cols-1 gap-3 border-b border-[#E2E8F0] px-6 py-5 last:border-b-0 md:grid-cols-12 md:gap-4"
              >
                <div className="col-span-4 text-sm font-semibold text-[#0A1128]">{c.row}</div>
                <div className="col-span-4 flex items-start gap-2 text-sm text-[#334155]">
                  <Check size={16} className="mt-1 shrink-0 text-[#E8772E]" />
                  <span>{c.qwis}</span>
                </div>
                <div className="col-span-4 flex items-start gap-2 text-sm text-[#94A3B8]">
                  <X size={16} className="mt-1 shrink-0 text-[#94A3B8]" />
                  <span>{c.typical}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
